import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet } from '../utils/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ResponsiveLogo } from '../components/ThemeLogo';
import PublicHeader from '../components/PublicHeader';
import {
  Search, Clock, ArrowRight, ChevronLeft, ChevronRight,
  User, Calendar, Tag, BookOpen,
} from 'lucide-react';

const CATEGORIES = [
  "Strategy", "Business Growth", "Leadership", "Decision Making",
  "Execution", "Startups", "Product", "Customer Success",
  "Case Studies", "Odapto Updates"
];

function FeaturedArticle({ post }) {
  if (!post) return null;
  return (
    <Link to={`/blog/${post.slug}`} className="block group" data-testid="featured-article">
      <div className="rounded-2xl overflow-hidden border border-border bg-card hover:shadow-xl transition-all">
        <div className="grid md:grid-cols-2 gap-0">
          <div className="aspect-[16/10] md:aspect-auto bg-muted overflow-hidden">
            {post.featured_image ? (
              <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-odapto-orange/20 to-odapto-teal/20 flex items-center justify-center min-h-[240px]">
                <BookOpen className="w-16 h-16 text-odapto-orange/40" />
              </div>
            )}
          </div>
          <div className="p-6 md:p-8 flex flex-col justify-center">
            <span className="text-xs font-semibold text-odapto-orange uppercase tracking-wider mb-2">{post.category} &middot; Featured</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-3 group-hover:text-odapto-orange transition-colors leading-tight">{post.title}</h2>
            <p className="text-muted-foreground text-sm mb-4 line-clamp-3">{post.summary}</p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><User className="w-3 h-3" /> {post.author_name}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.publish_date ? new Date(post.publish_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.reading_time} min read</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="group block" data-testid={`blog-card-${post.slug}`}>
      <div className="rounded-xl overflow-hidden border border-border bg-card hover:shadow-lg transition-all h-full flex flex-col">
        <div className="aspect-[16/10] bg-muted overflow-hidden">
          {post.featured_image || post.thumbnail ? (
            <img src={post.featured_image || post.thumbnail} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-odapto-orange/10 to-odapto-teal/10 flex items-center justify-center">
              <BookOpen className="w-10 h-10 text-odapto-orange/30" />
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col flex-1">
          <span className="text-[11px] font-semibold text-odapto-orange uppercase tracking-wider mb-2">{post.category}</span>
          <h3 className="font-bold text-lg mb-2 group-hover:text-odapto-orange transition-colors leading-snug line-clamp-2">{post.title}</h3>
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2 flex-1">{post.summary}</p>
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1"><User className="w-3 h-3" /> {post.author_name}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.publish_date ? new Date(post.publish_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
            </div>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.reading_time} min</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (activeCategory) params.set('category', activeCategory);
      params.set('page', page);
      params.set('limit', '12');

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts?${params}`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
        setFeatured(data.featured || null);
        setTotalPages(data.pages || 1);
      }
    } catch (err) {
      console.error('Failed to load blog posts:', err);
    }
    setLoading(false);
  }, [search, activeCategory, page]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPosts();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      <PublicHeader />

      {/* Hero */}
      <section className="py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-odapto-orange/5 to-background" data-testid="blog-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">Odapto Insights</h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto mb-8">
            Ideas, strategies, AI insights and practical frameworks to help businesses think better, decide faster and execute with confidence.
          </p>
          <form onSubmit={handleSearch} className="max-w-lg mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="pl-10 pr-4 h-11 rounded-full border-border"
              data-testid="blog-search-input"
            />
          </form>
        </div>
      </section>

      {/* Categories */}
      <section className="px-4 sm:px-6 pb-8" data-testid="blog-categories">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => { setActiveCategory(''); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${!activeCategory ? 'bg-odapto-orange text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              data-testid="blog-cat-all"
            >
              All
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${activeCategory === cat ? 'bg-odapto-orange text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                data-testid={`blog-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="px-4 sm:px-6 pb-16" data-testid="blog-content">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-odapto-orange border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 && !featured ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No articles yet</h3>
              <p className="text-muted-foreground">Check back soon for insights and strategies.</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {!activeCategory && !search && page === 1 && featured && (
                <div className="mb-12">
                  <FeaturedArticle post={featured} />
                </div>
              )}

              {/* Grid */}
              <div className="mb-8">
                <h2 className="text-xl font-bold mb-6">{activeCategory || 'Latest Articles'}</h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
                  {posts.filter(p => !featured || p.blog_id !== featured.blog_id || activeCategory || search || page > 1).map(post => (
                    <ArticleCard key={post.blog_id} post={post} />
                  ))}
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-8" data-testid="blog-pagination">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-4">Page {page} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#162F38] text-zinc-400 py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <ResponsiveLogo className="h-7 w-auto brightness-200" />
          </Link>
          <span className="text-xs">&copy; 2026 Odapto. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
